// client/src/components/pages/PeriodSummary/TicketsTable.js
//
// The "Tickets" tab of an expanded Period Summary group (ST-00174): the
// underlying tickets behind one Ref No / Order No, paged server-side.

import React, { useCallback, useEffect, useState } from 'react';
import { FaSortAmountDown, FaSortAmountUp, FaArrowRight } from 'react-icons/fa';
import Pagination from '../../common/Pagination';
import LoadingSpinner from '../../common/LoadingSpinner';
import { formatMiningDateTime } from '../../../utils/dateUtils';
import { getPeriodSummaryTickets } from '../../../services/periodSummaryService';
import { formatTonnes } from './utils';

const PAGE_SIZE = 50;

// Only these are accepted by the API's sort whitelist.
const COLUMNS = [
  { id: 'ticket_timestamp', label: 'Timestamp', sortable: true },
  { id: 'ticket_type', label: 'Type', sortable: true },
  { id: 'ticket_no', label: 'Ticket No', sortable: true },
  { id: 'deliveryNo', label: 'Delivery No', sortable: false },
  { id: 'product', label: 'Product', sortable: true },
  { id: 'route', label: 'Route', sortable: false },
  { id: 'dispatch_location', label: 'Dispatch', sortable: true },
  { id: 'receipt_location', label: 'Receipt', sortable: true },
  { id: 'haulier', label: 'Haulier', sortable: true },
  { id: 'reg_no', label: 'Reg No', sortable: true },
  { id: 'driver', label: 'Driver', sortable: false },
  { id: 'firstWeight', label: 'First t', sortable: false, numeric: true },
  { id: 'secondWeight', label: 'Second t', sortable: false, numeric: true },
  { id: 'nett_weight', label: 'Nett t', sortable: true, numeric: true },
];

const TypeBadge = ({ type }) => (
  <span
    className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
      type === 'Dispatch' ? 'bg-blue-50 text-blue-700' : 'bg-violet-50 text-violet-700'
    }`}
  >
    {type || '—'}
  </span>
);

const TicketsTable = ({ groupKey, query }) => {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ column: 'ticket_timestamp', dir: 'desc' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // A new key / period / grouping means a fresh listing.
  useEffect(() => {
    setPage(1);
  }, [
    groupKey,
    query.startDate,
    query.endDate,
    query.groupBy,
    query.ticketType,
    query.groupingId,
    query.dispatchLocation,
    query.receiptLocation,
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await getPeriodSummaryTickets({
        ...query,
        key: groupKey,
        page,
        pageSize: PAGE_SIZE,
        sort: sort.column,
        dir: sort.dir,
      });
      setData(body);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [groupKey, query, page, sort]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSort = (columnId) => {
    setPage(1);
    setSort((prev) =>
      prev.column === columnId
        ? { column: columnId, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { column: columnId, dir: 'desc' }
    );
  };

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-xs text-gray-500">
        <LoadingSpinner size="sm" /> Loading tickets…
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-3 p-2.5 bg-red-50 border border-red-200 text-red-700 text-xs rounded-md flex items-center justify-between gap-3">
        <span>{error}</span>
        <button
          type="button"
          onClick={load}
          className="px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-100"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between px-3 py-2 text-[11px] text-gray-500">
        <span>
          <span className="font-mono font-semibold text-slate-800">{total.toLocaleString()}</span>{' '}
          tickets
        </span>
        {loading && <LoadingSpinner size="sm" />}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="bg-gray-50 text-left">
              {COLUMNS.map((col) => {
                const active = sort.column === col.id;
                return (
                  <th
                    key={col.id}
                    scope="col"
                    className={`px-2.5 py-2 font-medium text-gray-500 whitespace-nowrap ${
                      col.numeric ? 'text-right' : ''
                    }`}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.id)}
                        aria-label={`Sort by ${col.label}`}
                        className={`inline-flex items-center gap-1 hover:text-slate-800 ${
                          active ? 'text-slate-800 font-semibold' : ''
                        }`}
                      >
                        {col.label}
                        {active &&
                          (sort.dir === 'asc' ? (
                            <FaSortAmountUp className="text-[8px]" />
                          ) : (
                            <FaSortAmountDown className="text-[8px]" />
                          ))}
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                );
              })}
              <th scope="col" className="px-2.5 py-2 font-medium text-gray-500">
                Amended
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.ticketUid} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-2.5 py-1.5 whitespace-nowrap font-mono text-gray-500">
                  {formatMiningDateTime(t.ticketTimestamp)}
                </td>
                <td className="px-2.5 py-1.5">
                  <TypeBadge type={t.ticketType} />
                </td>
                <td className="px-2.5 py-1.5 font-mono text-slate-800">{t.ticketNo || '—'}</td>
                <td className="px-2.5 py-1.5 font-mono text-gray-500">{t.deliveryNo || '—'}</td>
                <td className="px-2.5 py-1.5 text-slate-800">{t.product || '—'}</td>
                {/* The ticket's Route Grouping; the raw legs follow in their own columns. */}
                <td
                  className="px-2.5 py-1.5 text-slate-800 whitespace-nowrap"
                  title={t.route ? t.route.name : 'Not on a route grouping'}
                >
                  {t.route ? t.route.name : <span className="text-gray-400 italic">none</span>}
                </td>
                <td className="px-2.5 py-1.5 text-gray-500 whitespace-nowrap">
                  {t.dispatchLocation || '—'}
                </td>
                <td className="px-2.5 py-1.5 text-gray-500 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1">
                    <FaArrowRight className="text-[7px] text-gray-300" />
                    {t.receiptLocation || '—'}
                  </span>
                </td>
                <td className="px-2.5 py-1.5 text-gray-500">{t.haulier || '—'}</td>
                <td className="px-2.5 py-1.5 font-mono text-gray-500">{t.regNo || '—'}</td>
                <td className="px-2.5 py-1.5 text-gray-500">{t.driver || '—'}</td>
                <td className="px-2.5 py-1.5 text-right font-mono">
                  {formatTonnes(t.firstWeight)}
                </td>
                <td className="px-2.5 py-1.5 text-right font-mono">
                  {formatTonnes(t.secondWeight)}
                </td>
                <td className="px-2.5 py-1.5 text-right font-mono font-semibold text-slate-800">
                  {formatTonnes(t.nettWeight)}
                </td>
                <td className="px-2.5 py-1.5">
                  {t.isAmended ? (
                    <span
                      className="inline-block px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-medium"
                      title={t.amendmentStatus || 'Amended'}
                    >
                      Amended
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={COLUMNS.length + 1}
                  className="px-3 py-6 text-center text-gray-400 text-xs"
                >
                  No tickets for this group
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="py-3">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
};

export default TicketsTable;
